function stripInlineMarkdown(text: string): string {
  return text.replace(/[*_`~]/g, "");
}

export function stripMarkdownForDisplay(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;

  // Table-like: split by | (with optional spaces) and newlines, trim, filter empty, join with ", "
  if (trimmed.includes("|")) {
    const cells = trimmed
      .split(/\n|\s*\|\s*/)
      .map((cell) => cell.trim())
      .filter(Boolean);
    return cells.join(", ");
  }

  // List-like: strip list markers and join lines with ", "
  const hasListMarkers =
    /^\s*[-*+]\s+/m.test(trimmed) || /^\s*\d+\.\s+/m.test(trimmed);
  if (hasListMarkers) {
    const stripped = trimmed
      .replace(/^\s*[-*+]\s+/gm, "")
      .replace(/^\s*\d+\.\s+/gm, "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    return stripped.join(", ");
  }

  return stripInlineMarkdown(trimmed);
}

type MentionUser = {
  firstName?: string | null;
  lastName?: string | null;
  email: string;
};

function formatMentionUserName(user: MentionUser): string {
  if (user.firstName || user.lastName) {
    return `${user.firstName || ""} ${user.lastName || ""}`.trim();
  }
  return user.email;
}

/**
 * Replace stored @email mentions with @Full Name for runtime display.
 * Keeps the original @email token when no matching org user is found.
 */
export function replaceEmailMentionsForDisplay(
  content: string,
  orgUsers: MentionUser[]
): string {
  if (!content || orgUsers.length === 0) return content;

  const usersByEmail = new Map<string, MentionUser>();
  for (const user of orgUsers) {
    usersByEmail.set(user.email.toLowerCase(), user);
  }

  const mentionRegex = /@([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/gi;
  return content.replace(mentionRegex, (match, email: string) => {
    const matchedUser = usersByEmail.get(email.toLowerCase());
    if (!matchedUser) return match;
    return `@${formatMentionUserName(matchedUser)}`;
  });
}
